import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { payrollAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const PayrollManagement = () => {
  const { currentUser, hasPermission } = useAuth();
  const isPrivileged = hasPermission('employee_view');
  const [showModal, setShowModal] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form, setForm] = useState({ employee: '', month: new Date().toISOString().slice(0, 7), basicSalary: '', bonus: '', deductions: '' });

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const [y, m] = filterMonth.split('-');
      const res = await payrollAPI.getAll({ year: y, month: m });
      setPayrolls(res?.data || res || []);
    } catch (e) { setError('Failed to load payroll'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPayrolls();
    if (isPrivileged) employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
  }, [filterMonth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const [y, m] = form.month.split('-');
      await payrollAPI.create({ ...form, year: parseInt(y), month: parseInt(m), netSalary: (parseFloat(form.basicSalary) || 0) + (parseFloat(form.bonus) || 0) - (parseFloat(form.deductions) || 0) });
      setShowModal(false);
      fetchPayrolls();
    } catch (err) { setError('Failed to process payroll'); }
  };

  const fmt = (n) => n !== undefined && n !== null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

  return (
    <div className="payroll-management">
      <div className="module-header">
        <h2>Payroll Management</h2>
        {isPrivileged && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Process Payroll
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      <div className="filter-section">
        <div className="filter-group">
          <label>Month:</label>
          <input type="month" className="form-input" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} />
        </div>
        <button className="btn btn-secondary" onClick={fetchPayrolls}>Filter</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p>Loading...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {isPrivileged && <th>Emp ID</th>}
                {isPrivileged && <th>Name</th>}
                <th>Month/Year</th>
                <th>Basic</th>
                <th>Bonus</th>
                <th>Deductions</th>
                <th>Net Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.length === 0
                ? <tr><td colSpan="9" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No payroll records found</td></tr>
                : payrolls.map(p => (
                  <tr key={p._id}>
                    {isPrivileged && <td>{p.employee?.employeeId || '—'}</td>}
                    {isPrivileged && <td>{p.employee?.name || '—'}</td>}
                    <td>{p.month}/{p.year}</td>
                    <td>{fmt(p.basicSalary)}</td>
                    <td>{fmt(p.bonus)}</td>
                    <td>{fmt(p.deductions)}</td>
                    <td style={{ fontWeight: 600 }}>{fmt(p.netSalary)}</td>
                    <td>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: p.status === 'paid' ? '#d1fae5' : '#fef3c7',
                        color: p.status === 'paid' ? '#065f46' : '#92400e',
                        textTransform: 'capitalize'
                      }}>{p.status || 'pending'}</span>
                    </td>
                    <td>
                      <button className="btn-icon" title="View"><i className="fas fa-eye"></i></button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {isPrivileged && (
        <Modal show={showModal} onClose={() => setShowModal(false)} title="Process Payroll">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Employee</label>
              <select className="form-input" value={form.employee} onChange={e => setForm({ ...form, employee: e.target.value })} required>
                <option value="">Select Employee</option>
                {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Month</label>
              <input type="month" className="form-input" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Basic Salary (₹)</label>
                <input type="number" className="form-input" placeholder="50000" value={form.basicSalary} onChange={e => setForm({ ...form, basicSalary: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Bonus (₹)</label>
                <input type="number" className="form-input" placeholder="0" value={form.bonus} onChange={e => setForm({ ...form, bonus: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Deductions (₹)</label>
              <input type="number" className="form-input" placeholder="0" value={form.deductions} onChange={e => setForm({ ...form, deductions: e.target.value })} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Process Payroll</button>
            </div>
          </form>
        </Modal>
      )}

      <style jsx>{`
        .filter-section { background: white; padding: 20px; border-radius: 12px; margin-bottom: 20px; display: flex; gap: 20px; align-items: end; flex-wrap: wrap; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
        .filter-group { display: flex; flex-direction: column; gap: 8px; }
        .filter-group label { font-size: 14px; font-weight: 500; color: var(--text-primary); }
      `}</style>
    </div>
  );
};

export default PayrollManagement;
